import {
  buildValueDef,
  normalizeSelectOptions,
  selectOptionValueMeta,
  buildTableColFields,
  schemaValueDefFromField,
} from '../value-def-helpers';

/**
 * 表单控件值结构描述：在 methods 中通过 getValueDef() 动态读取 config / fields
 */
export default {
  methods: {
    _refOf(key) {
      if (!key) {
        return null;
      }
      let r = this.$refs[key];
      return Array.isArray(r) ? r[0] : r;
    },
    /** 从子字段配置 + 已挂载子组件递归收集值定义（容器用） */
    collectFieldsValueDef(fields) {
      const containers = this.$store.getters['forms/containers'];
      const acl = this.acl || {};
      const result = [];
      (fields || []).forEach((f) => {
        if (!f || (f.fullkey && acl[f.fullkey] === 2)) {
          return;
        }
        const ref = this._refOf(f.key);
        if (ref && typeof ref.getValueDef === 'function') {
          const d = ref.getValueDef();
          if (d == null) {
            return;
          }
          if (Array.isArray(d)) {
            result.push(...d);
          } else {
            result.push(d);
          }
          return;
        }
        if (containers.includes(f.control)) {
          result.push(...this.collectFieldsValueDef(f.fields));
          return;
        }
        const schemaDef = schemaValueDefFromField(f, (childFields) =>
          this.collectFieldsValueDef(childFields),
        );
        if (schemaDef) {
          if (Array.isArray(schemaDef)) {
            result.push(...schemaDef);
          } else {
            result.push(schemaDef);
          }
        }
      });
      return result;
    },
    _fieldKey() {
      return this.fkey || (this.$attrs && this.$attrs.fkey);
    },
    _fieldName() {
      return this.label;
    },
    _fieldConfig() {
      return this.config || {};
    },
    valueDefString() {
      return buildValueDef({
        type: 'string',
        key: this._fieldKey(),
        name: this._fieldName(),
        option: {...this._fieldConfig()},
      });
    },
    valueDefNumber() {
      return buildValueDef({
        type: 'number',
        key: this._fieldKey(),
        name: this._fieldName(),
        option: {...this._fieldConfig()},
      });
    },
    valueDefSelect() {
      const cfg = this._fieldConfig();
      const multiple = !!cfg.multiple;
      return buildValueDef({
        type: multiple ? 'array' : 'string',
        key: this._fieldKey(),
        name: this._fieldName(),
        option: {
          multiple,
          ...selectOptionValueMeta(cfg),
          options: normalizeSelectOptions(cfg),
          url: cfg.url,
        },
      });
    },
    valueDefCheckArray() {
      const cfg = this._fieldConfig();
      return buildValueDef({
        type: 'array',
        key: this._fieldKey(),
        name: this._fieldName(),
        option: {
          ...selectOptionValueMeta(cfg),
          options: normalizeSelectOptions(cfg),
        },
      });
    },
    valueDefFile() {
      return buildValueDef({
        type: 'array',
        key: this._fieldKey(),
        name: this._fieldName(),
        option: {
          description: '文件列表',
          ...this._fieldConfig(),
        },
      });
    },
    valueDefDatetimeRange() {
      return buildValueDef({
        type: 'array',
        key: this._fieldKey(),
        name: this._fieldName(),
        option: {
          length: 2,
          itemType: 'string',
          description: '[开始时间, 结束时间]',
          ...this._fieldConfig(),
        },
      });
    },
    valueDefUserSelect() {
      const multiple = !!this._fieldConfig().multiple;
      return buildValueDef({
        type: multiple ? 'array' : 'object',
        key: this._fieldKey(),
        name: this._fieldName(),
        option: {multiple, ...this._fieldConfig()},
      });
    },
    valueDefTable() {
      const cols = this._fieldConfig().cols || [];
      return buildValueDef({
        type: 'array',
        key: this._fieldKey(),
        name: this._fieldName(),
        option: {itemType: 'object', ...this._fieldConfig()},
        fields: buildTableColFields(cols),
      });
    },
    valueDefContractSplitTable() {
      return buildValueDef({
        type: 'array',
        key: this._fieldKey(),
        name: this._fieldName(),
        option: {
          itemType: 'object',
          amountKey: this._fieldConfig().amountKey,
        },
        fields: [
          {type: 'string', key: 'business_type', name: '服务类型'},
          {type: 'number', key: 'amount', name: '金额'},
          {type: 'string', key: 'type', name: '类型'},
          {type: 'number', key: 'deduct_amount', name: '扣除金额'},
          {type: 'boolean', key: 'unbilling', name: '对方开票'},
        ],
      });
    },
    valueDefZzlPreselect() {
      const cfg = this._fieldConfig();
      const baseOptions = [
        {key: 0, name: '选用'},
        {key: 1, name: '不选用'},
        {key: 2, name: '自定义'},
      ];
      const options = cfg.simple !== false ?
        baseOptions.map((o) => ({key: o.name, name: o.name})) :
        baseOptions;
      return buildValueDef({
        type: 'string',
        key: this._fieldKey(),
        name: this._fieldName(),
        option: {
          ...selectOptionValueMeta({simple: cfg.simple !== false, ...cfg}),
          options,
        },
      });
    },
    valueDefNestedForm() {
      const fieldKey = this._fieldKey();
      const nested = this.$refs.form;
      const formDefFields = (this.formDef && this.formDef.fields) || this.fields || [];
      let childFields = [];
      if (nested && typeof nested.getEditableFieldsDef === 'function') {
        childFields = nested.getEditableFieldsDef();
      }
      if (!childFields.length && formDefFields.length) {
        childFields = this.collectFieldsValueDef(formDefFields);
      }
      return buildValueDef({
        type: 'object',
        key: fieldKey,
        name: this._fieldName(),
        option: {
          model: this._fieldConfig().model,
          description: fieldKey ?
            `内嵌表单，JSON 中键名必须为 "${fieldKey}"（不要用中文名称作键）` :
            '内嵌表单对象',
        },
        fields: childFields,
      });
    },
    valueDefMultiForm() {
      const fieldKey = this._fieldKey();
      const forms = this.$refs.form;
      let itemFields = [];
      const templateForm = Array.isArray(forms) ?
        forms.find((f) => f && typeof f.getEditableFieldsDef === 'function') :
        forms;
      if (templateForm) {
        itemFields = templateForm.getEditableFieldsDef();
      } else if (this.formDef && Array.isArray(this.formDef.fields) && this.formDef.fields.length) {
        itemFields = this.collectFieldsValueDef(this.formDef.fields);
      } else {
        itemFields = this.collectFieldsValueDef(this.fields || []);
      }
      return buildValueDef({
        type: 'array',
        key: fieldKey,
        name: this._fieldName(),
        option: {
          itemType: 'object',
          model: this._fieldConfig().model,
          description: fieldKey ?
            `多重表(MultiForm)：JSON 键名必须为 "${fieldKey}"，值为对象数组；每项为一条记录，键名见 fields（不要用中文 name 作键）` :
            '多重表(MultiForm)：值为对象数组，每项为一条记录',
          valueShape: `[{ ${(itemFields || []).map((f) => f.key).filter(Boolean).join(', ')} }]`,
        },
        fields: itemFields,
      });
    },
  },
};
